import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { BackdropLoader, DatePicker, SelectMenu } from '@livable/saas-react-components';
import { MenuItem, Paper } from '@mui/material';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AllocationFromHistory from 'src/components/allocations/AllocationHistory';
import NoAllocations from 'src/components/allocations/AllocationProgress/NoAllocations';
import { PROPERTY_STATUS_ENUM } from 'src/constants/property';
import { useStore } from 'src/stores';
import { fullMonthName, monthNames, monthsToNumber } from 'src/utils/date';
import useStyles from './styles';
const AllocationHistory = observer(() => {
  const classes = useStyles();
  const store = useStore();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const selectedProperty = store.allocations.selectedProperty;
  const setSelectedProperty = store.allocations.setSelectedProperty;
  const dateTime = moment().toDate();
  const month = dateTime.toString().substring(4, 7);
  const year = dateTime.getFullYear();
  const _useState = useState(undefined),
    _useState2 = _slicedToArray(_useState, 2),
    fromMonth = _useState2[0],
    setFromMonth = _useState2[1];
  const _useState3 = useState(undefined),
    _useState4 = _slicedToArray(_useState3, 2),
    toMonth = _useState4[0],
    setToMonth = _useState4[1];
  const _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isLoading = _useState6[0],
    setIsLoading = _useState6[1];
  const getDate = dateFromPicker => {
    const fromM = monthsToNumber[dateFromPicker.substring(4, 7)];
    const fromY = dateFromPicker.substring(11, 15);
    return "".concat(fromY).concat(fromM) * 1;
  };
  const getAllocationHistory = async data => {
    const resp = await store.allocations.getAllocationHistoryAsync.run({
      data,
      applyToHomeDashboard: false
    });
    return resp;
  };
  const getCompanyProperties = async () => {
    const resp = await store.company.getCompanyPropertiesAsync.run();
    return resp;
  };
  const getUtilityInfo = async function () {
    let _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
        propertyId: undefined
      },
      propertyId = _ref.propertyId;
    if (!store.account.featureFlagsStore.getIsBillAutomationEnabled || !store.account.featureFlagsStore.getIsBillingTemplatesEnabled) {
      const resp = await store.utilityProviders.getUtilityInfoAsync.run({
        utilityIds: store.allocations.allocationUtilityIds
      });
      return resp;
    } else {
      const resp = await store.utilityProviders.getPropertyUtilityInfoAsync.run({
        utilityIds: store.allocations.allocationUtilityIds,
        propertyId: propertyId !== null && propertyId !== void 0 ? propertyId : selectedProperty
      });
      return resp;
    }
  };
  useEffect(() => {
    setIsLoading(true);
    let propertyId;
    getCompanyProperties().then(() => {
      var _companyBuildings$;
      const companyBuildings = [...store.company.propertiesList.filter(c => c.propertyStatus === PROPERTY_STATUS_ENUM.Active && c.utilities.length).map(property => ({
        propertyAddress: property.propertyAddress,
        propertyId: property.propertyId
      }))];
      propertyId = (_companyBuildings$ = companyBuildings[0]) === null || _companyBuildings$ === void 0 ? void 0 : _companyBuildings$.propertyId;
    }).then(() => {
      setSelectedProperty(propertyId);
      store.allocations.clearAllocationHistory();
      const from = moment().startOf('month').subtract(12, 'month');
      const to = moment().endOf('month');
      setFromMonth(from.toDate());
      setToMonth(to.toDate());
      return getAllocationHistory({
        endMonth: to.month() + 1,
        endYear: to.year(),
        startMonth: from.month() + 1,
        startYear: from.year(),
        propertyId
      });
    }).then(() => getUtilityInfo({
      propertyId
    })).then(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (fromMonth && toMonth && selectedProperty) {
      setIsLoading(true);
      getAllocationHistory({
        endMonth: monthsToNumber[toMonth.toString().substring(4, 7)],
        endYear: toMonth.toString().substring(11, 15),
        startMonth: monthsToNumber[fromMonth.toString().substring(4, 7)],
        startYear: fromMonth.toString().substring(11, 15),
        propertyId: selectedProperty
      }).then(() => getUtilityInfo()).then(() => setIsLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromMonth, toMonth, selectedProperty]);
  const propertiesList = store.company.propertiesList.filter(p => p.propertyStatus === PROPERTY_STATUS_ENUM.Active && p.utilities.length).map(property => ({
    propertyAddress: property.propertyAddress,
    propertyId: property.propertyId
  }));
  const handleBuildingChange = event => {
    if (event.target.value) {
      setSelectedProperty(event.target.value);
      store.allocations.clearAllocationHistory();
    }
  };
  const handleFromMonth = event => {
    const fromNumber = getDate(event.toString());
    let toNumber = getDate(toMonth.toString());
    if (fromNumber >= toNumber) {
      toNumber = fromNumber;
      setToMonth(event);
    }
    setFromMonth(event);
  };
  const handleToMonth = event => {
    let fromNumber = getDate(fromMonth.toString());
    const toNumber = getDate(event.toString());
    if (toNumber <= fromNumber) {
      fromNumber = toNumber;
      setFromMonth(event);
    }
    setToMonth(event);
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Paper, {
    className: classes.paper
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.searchBar
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.selector
  }, /*#__PURE__*/React.createElement(BackdropLoader, {
    isOpen: isLoading
  }), propertiesList.length ? /*#__PURE__*/React.createElement(SelectMenu, {
    id: "building-selector",
    value: selectedProperty,
    label: t('allocations.display_history_for'),
    labelId: "building-selector-label",
    onChange: handleBuildingChange
  }, propertiesList.map(building => /*#__PURE__*/React.createElement(MenuItem, {
    key: building.propertyId,
    value: building.propertyId
  }, building.propertyAddress))) : /*#__PURE__*/React.createElement(SelectMenu, {
    disabled: true,
    id: "initial-building-selector",
    label: t('allocations.no_building'),
    value: ''
  })), /*#__PURE__*/React.createElement("div", {
    className: classes.chargesRangeDatePicker
  }, /*#__PURE__*/React.createElement(DatePicker, {
    value: fromMonth,
    onChange: date => handleFromMonth(date),
    format: "MMMM yyyy",
    label: t('general.from'),
    showMonthYearPicker: true
  })), /*#__PURE__*/React.createElement("div", {
    className: classes.chargesRangeDatePicker
  }, /*#__PURE__*/React.createElement(DatePicker, {
    value: toMonth,
    onChange: date => handleToMonth(date),
    format: "MMMM yyyy",
    label: t('general.to'),
    showMonthYearPicker: true
  })))), store.allocations.allocationHistory.length ? store.allocations.allocationHistory.map(allocation => /*#__PURE__*/React.createElement(AllocationFromHistory, {
    allocationCycleId: allocation.allocationCycleId,
    key: allocation.allocationCycleId,
    month: monthNames[allocation.month - 1],
    statementMonth: monthNames[allocation.month],
    selectedBuilding: selectedProperty,
    year: allocation.year,
    utilities: allocation.utilities,
    propertyData: propertiesList.find(building => building.propertyId === selectedProperty)
    // billingCycleId={allocation.billingCycleId}
  })) : /*#__PURE__*/React.createElement(NoAllocations, {
    name: "".concat(fullMonthName[month], " ").concat(year)
  }));
});
export default AllocationHistory;